import {
    Bounds, Point, Matrix, Distance,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';
import Model from './model';

/**
 * @class AbstractGroupItem
 * @extends AbstractItem
 */
const AbstractGroupItem = AbstractItem.extend({
    /**
     * Center point of group at the time the group is selected.
     * Used to rotate around the same center point in several steps.
     *
     * @property {Point} rotationCenter
     */
    rotationCenter: null,

    items: [],

    _rotationOffsets: {},
    _positionOffsets: {},

    defaults: {
        /**
         * Name of the item
         * @property {String} attributes.name
         */
        name: null,

        /**
         * Current rotation
         * @property {Number} attributes.rotation
         */
        rotation: 0,

        /**
         * Extended path coordinates
         * @property {Array} attributes.ext
         */
        ext: [],

        /**
         * Original path coordinates
         * @property {Array} attributes.orig
         */
        orig: [],

        /**
         * Safety margin path coordinates
         * @property {Array} attributes.safety
         */
        safety: [],

        /**
         * Depth that the item sinks into the foam
         * @property {Number} attributes.depth
         */
        depth: 0,

        /**
         * Total depth of the item
         * @property {Number} attributes.totalDepth
         */
        totalDepth: 0,
    },

    /**
     * Array with grouped items
     *
     * @property {Array} items
     */

    /**
     * Initializes a new group item
     *
     * @constructor
     */
    initialize() {
        this.supportsRotation = true;
        this.supportsPositioning = true;
        this.supportsCollisions = true;
        this.supportsOutOfFoam = true;
        this.supportsGuides = true;

        this._rotationOffsets = {};
        this._positionOffsets = {};
        this.items = [];

        AbstractItem.prototype.initialize.call(this);

        this.on('select', this.initRotationCenter, this);
        this.on('all', this.cascadeItemEvents, this);
    },

    getBagLines() {
        const segments = [];

        this.items.forEach(item => segments.push(...item.getBagLines()));

        return this.getLines(segments);
    },

    getMarginLines() {
        const segments = [];

        this.items.forEach(item => segments.push(...item.getMarginLines()));

        return this.getLines(segments);
    },

    getOriginalLines() {
        const segments = [];

        this.items.forEach(item => segments.push(...item.getOriginalLines()));

        return this.getLines(segments);
    },

    getBounds() {
        let bounds;

        this.items.forEach((item) => {
            if (!bounds) {
                bounds = item.getBounds();
            } else {
                bounds = bounds.merge(item.getBounds());
            }
        });

        if (!bounds) {
            bounds = new Bounds(new Point(0, 0));
        }

        return bounds;
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        let bounds;

        this.items.forEach((item) => {
            if (bounds) {
                bounds.merge(item.getMarginBounds());
            } else {
                bounds = item.getMarginBounds();
            }
        });

        return bounds;
    },

    set(key, value, optionsParam) {
        let attrs = key;
        let options = value || {};

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key !== 'object') {
            attrs = { [key]: value };
            options = optionsParam || {};
        }

        options.center = options.center || this.get('position');

        // normalize rotation
        if (attrs.rotation !== undefined) {
            attrs = this._sanitizeRotationValue(attrs);
        }

        const groupPosition = attrs.position || this.get('position');
        const groupRotation = attrs.rotation === undefined ? this.get('rotation') : attrs.rotation;

        this.items.forEach((item) => {
            const itemAttributes = {};
            const matrix = new Matrix();

            if (attrs.rotation !== undefined) {
                itemAttributes.rotation = groupRotation + this._rotationOffsets[item.cid];
            }

            if (attrs.position !== undefined) {
                matrix
                    .rotate(groupRotation, groupPosition)
                    .translate(this._positionOffsets[item.cid].point.x, this._positionOffsets[item.cid].point.y);

                itemAttributes.position = groupPosition.clone().transform(matrix);
            }

            item.set(itemAttributes, options);
        });

        // We don't want to use the stuff from the abstract setter
        Model.prototype.set.call(this, key, value, optionsParam);
    },

    get(key, ...args) {
        if (key === 'position') {
            return this.getCentroidOfItemPositions();
        }
        return AbstractItem.prototype.get.call(this, key, ...args);
    },

    getCentroidOfItemPositions() {
        const items = this.items || [];
        let x = 0;
        let y = 0;

        for (let i = 0; i < items.length; i += 1) {
            const position = items[i].get('position');
            x += position.x;
            y += position.y;
        }

        return new Point(
            x ? (x / items.length) : 0,
            y ? (y / items.length) : 0,
        );
    },

    /**
     * Adds an item to the group
     *
     * @param {AbstractItem} item Item to add
     * @return {GroupItem} this
     * @chainable
     */
    addItem(item) {
        if (!(item instanceof AbstractItem)) {
            throw new Error('Only instances of AbstractItem accepted');
        }

        item.setParent(this);
        this.items.push(item);

        let depth = this.get('depth');
        let totalDepth = this.get('totalDepth');
        const groupRotation = this.get('rotation');
        const groupPosition = this.get('position');

        this.items.forEach((childItem) => {
            const matrix = new Matrix();

            matrix
                .rotate(groupRotation, groupPosition)
                .invert();

            const childPosition = childItem.get('position').clone().transform(matrix);

            this._rotationOffsets[childItem.cid] = childItem.get('rotation') - groupRotation;
            this._positionOffsets[childItem.cid] = new Distance(groupPosition, childPosition);

            if (!depth || depth < childItem.get('depth')) {
                depth = childItem.get('depth');
                this.set('depth', depth);
            }
            if (!totalDepth || totalDepth < childItem.get('totalDepth')) {
                totalDepth = childItem.get('totalDepth');
                this.set('totalDepth', totalDepth);
            }
        });

        return this;
    },

    /**
     * Adds an item to the group
     *
     * @param {Collection} items Items to add
     *
     * @return {GroupItem} this
     *
     * @chainable
     */
    addItems(items) {
        items.forEach(item => this.addItem(item));

        return this;
    },

    /**
     *
     * @param {AbstractItem} item
     */
    removeItem(item) {
        const index = this.items.indexOf(item);

        if (index >= 0) {
            // item.trigger('delete', item, this);
            item.setParent(null);
            this.items.splice(index, 1);
            delete this._positionOffsets[item.cid];
            delete this._rotationOffsets[item.cid];
        }
    },

    /**
     * Remove all items
     */
    clearItems() {
        for (let i = 0; i < this.items.length; i += 1) {
            const item = this.items[i];
            item.trigger('delete', this.items[i], this);
            item.setParent(null);
        }

        this.items = [];
        this._positionOffsets = {};
        this._rotationOffsets = {};
    },

    /**
     * Get items of group.
     *
     * @return {Array}
     */
    getItems() {
        return this.items;
    },

    /**
     * Set rotation center to center of bounds.
     */
    initRotationCenter() {
        this.rotationCenter = this.getBounds().getCenter();
    },

    /**
     * Callback to cascade all relevant events of a group to the members.
     *
     * @param {string} eventName Name of the triggered event
     */
    cascadeItemEvents(eventName) {
        if (eventName === 'select' || eventName === 'deselect' || eventName === 'delete' || eventName === 'remove') {
            this.cascadeItemEvent(eventName);
        }
    },

    /**
     * Cascade a single event to the group members.
     *
     * @param {string} eventName Name of the triggered event
     */
    cascadeItemEvent(eventName) {
        this.items.forEach(item => item.trigger(eventName, item));
    },

    /**
     * Check if rotation is changed in current change set.
     *
     * @param {Object} changes
     *
     * @return {boolean}
     *
     * @private
     */
    isRotationChanged(changes) {
        return changes && changes.rotation !== undefined;
    },

    /**
     * Check if position is changed in current change set.
     *
     * @param {Object} changes
     *
     * @return {boolean}
     *
     * @private
     */
    isPositionChanged(changes) {
        return changes && changes.position !== undefined && changes.position.getDistance();
    },

    /**
     * Move item by distance.
     *
     * @param {Distance} distance
     */
    move(distance) {
        const position = this.get('position').move(distance);
        this.set('position', position);
    },

    /**
     * Add special processing for groups.
     *
     * @return {GroupItem}
     */
    clone(...args) {
        const clone = AbstractItem.prototype.clone.apply(this, args);

        clone.items = this.cloneItems();

        return clone;
    },

    /**
     * Clone child items of current group.
     *
     * @return {Array} cloned items
     */
    cloneItems() {
        return this.items.map((item) => {
            if (!(item instanceof AbstractItem)) {
                throw new Error('group expects array of editor items');
            }

            const clone = item.clone();
            clone.id = null;
            return clone;
        });
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);
        const children = this.getItems().map(item => item.toJSON());

        return {
            itemId: attributes.itemId,
            rotation: attributes.rotation,
            children,
            type: attributes.type,
        };
    },

    /**
     * Returns a list of all items contained in this group and all of its
     * children. This method will be recursively called for all Child groups.
     *
     * @return {AbstractItem[]} Array of items
     */
    resolveGroupItems() {
        const result = [];

        this.getItems().forEach((item) => {
            if (item instanceof AbstractGroupItem) {
                result.push(...item.resolveGroupItems());
            } else {
                result.push(item);
            }
        });

        return result;
    },

    getMarginTree() {
        return null;
    },

    getBagTree() {
        return null;
    },

    getArea() {
        return this.getItems().reduce((total, item) => total + item.getArea(), 0);
    },
});

export default AbstractGroupItem;
